import { useMemo, useState } from "react";
import type { RoleRecord } from "../../shared/models";
import { ApiError, apiRequest } from "../lib/api";

interface AddCrewAccountsPageProps {
  roles: RoleRecord[];
  existingEmails: string[];
  onImported: () => Promise<void> | void;
}

type Mode = "manual" | "csv";

interface DraftRow {
  name: string;
  email: string;
  phone: string;
  role_ids: number[];
  roles_raw?: string;
  errors: string[];
}

interface ParsedCsvPreview {
  headers: string[];
  rows: DraftRow[];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emptyRow(): DraftRow {
  return {
    name: "",
    email: "",
    phone: "",
    role_ids: [],
    roles_raw: "",
    errors: [],
  };
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

function parseCsv(text: string) {
  const rows: string[][] = [];
  let current = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const next = text[index + 1];

    if (character === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (character === "," && !inQuotes) {
      row.push(current);
      current = "";
      continue;
    }

    if ((character === "\n" || character === "\r") && !inQuotes) {
      if (character === "\r" && next === "\n") {
        index += 1;
      }
      row.push(current);
      if (row.some((cell) => cell.trim().length > 0)) {
        rows.push(row);
      }
      row = [];
      current = "";
      continue;
    }

    current += character;
  }

  row.push(current);
  if (row.some((cell) => cell.trim().length > 0)) {
    rows.push(row);
  }

  return rows;
}

function validateRows(rows: DraftRow[], roles: RoleRecord[], existingEmails: string[]) {
  const roleMap = new Map(roles.map((role) => [role.id, role]));
  const roleNameMap = new Map(roles.map((role) => [role.name.trim().toLowerCase(), role.id]));
  const normalizedExisting = new Set(existingEmails.map(normalizeEmail));
  const emails = rows.map((row) => normalizeEmail(row.email));

  return rows.map((row, index) => {
    const errors: string[] = [];
    const role_ids = [...new Set(row.role_ids)].filter((role_id) => roleMap.has(role_id));

    if (!row.name.trim()) {
      errors.push("Name is required");
    }
    if (!row.email.trim()) {
      errors.push("Email is required");
    } else if (!EMAIL_PATTERN.test(normalizeEmail(row.email))) {
      errors.push("Invalid email");
    }
    if (!row.phone.trim()) {
      errors.push("Phone number is required");
    }
    if (emails.filter((email) => email === emails[index]).length > 1 && row.email.trim()) {
      errors.push("Duplicate email in this batch");
    }
    if (normalizedExisting.has(normalizeEmail(row.email)) && row.email.trim()) {
      errors.push("Duplicate email already exists");
    }
    if (role_ids.length === 0) {
      errors.push("At least one role is required");
    }

    if (row.roles_raw !== undefined) {
      const parsedRoleIds = row.roles_raw
        .split("|")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((roleName) => {
          const role_id = roleNameMap.get(roleName.toLowerCase());
          if (!role_id) {
            errors.push(`Unknown role name: ${roleName}`);
            return null;
          }
          return role_id;
        })
        .filter((value): value is number => typeof value === "number");
      return {
        ...row,
        role_ids: [...new Set(parsedRoleIds)],
        errors,
      };
    }

    return {
      ...row,
      role_ids,
      errors,
    };
  });
}

export function AddCrewAccountsPage({ roles, existingEmails, onImported }: AddCrewAccountsPageProps) {
  const [mode, setMode] = useState<Mode>("manual");
  const [manualRows, setManualRows] = useState<DraftRow[]>([emptyRow()]);
  const [csvPreview, setCsvPreview] = useState<ParsedCsvPreview | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const activeRoles = useMemo(() => roles.filter((role) => role.active === 1), [roles]);
  const validatedManualRows = useMemo(() => validateRows(manualRows, activeRoles, existingEmails), [manualRows, activeRoles, existingEmails]);
  const validatedCsvRows = useMemo(
    () => (csvPreview ? validateRows(csvPreview.rows, activeRoles, existingEmails) : []),
    [csvPreview, activeRoles, existingEmails],
  );

  function updateManualRow(index: number, patch: Partial<DraftRow>) {
    setManualRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
  }

  function downloadCsvTemplate() {
    const template = [
      "name,email,phone,roles",
      'Alex Carter,alex@example.com,555-0103,"FOH Engineer|System Engineer"',
      'Brooke Ellis,brooke@example.com,555-0104,"Monitor Engineer|Monitor Tech"',
      'Casey Nguyen,casey@example.com,555-0105,"FOH Engineer|RF Tech"',
      'Devon Brooks,devon@example.com,555-0106,"Stage Patch|Fly Tech"',
      'Ellis Jordan,ellis@example.com,555-0107,"Comms|RF Tech"',
    ].join("\n");
    const blob = new Blob([template], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "crew-accounts-template.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function toggleManualRole(index: number, role_id: number) {
    setManualRows((current) => current.map((row, rowIndex) => {
      if (rowIndex !== index) {
        return row;
      }
      return {
        ...row,
        role_ids: row.role_ids.includes(role_id)
          ? row.role_ids.filter((value) => value !== role_id)
          : [...row.role_ids, role_id],
      };
    }));
  }

  async function submitRows(rows: DraftRow[]) {
    try {
      const response = await apiRequest<{ created_count: number }>("/api/admin/users/batch", {
        method: "POST",
        body: JSON.stringify({
          rows: rows.map((row) => ({
            name: row.name.trim(),
            email: row.email.trim(),
            phone: row.phone.trim(),
            role_ids: row.role_ids,
          })),
        }),
      });
      await onImported();
      setSummary(`Created ${response.created_count} crew account${response.created_count === 1 ? "" : "s"}. Temporary password: CrewOps123!`);
      return true;
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray((caught.details as { row_errors?: unknown[] })?.row_errors)) {
        const row_errors = (caught.details as { row_errors: Array<{ index: number; errors: string[] }> }).row_errors;
        const applyErrors = (currentRows: DraftRow[]) => currentRows.map((row, index) => ({
          ...row,
          errors: row_errors.find((item) => item.index === index)?.errors ?? row.errors,
        }));
        if (mode === "manual") {
          setManualRows((current) => applyErrors(current));
        } else {
          setCsvPreview((current) => current ? { ...current, rows: applyErrors(current.rows) } : current);
        }
      }
      setSummary(caught instanceof Error ? caught.message : "Could not create crew accounts");
      return false;
    }
  }

  async function handleManualSave() {
    const rows = validatedManualRows;
    setManualRows(rows);
    if (rows.some((row) => row.errors.length > 0)) {
      setSummary("Fix the highlighted rows before saving.");
      return;
    }
    const success = await submitRows(rows);
    if (success) {
      setManualRows([emptyRow()]);
    }
  }

  async function handleCsvImport() {
    if (!csvPreview) {
      return;
    }
    const rows = validatedCsvRows;
    setCsvPreview((current) => current ? { ...current, rows } : current);
    if (rows.some((row) => row.errors.length > 0)) {
      setSummary("Fix the CSV validation errors before import.");
      return;
    }
    const success = await submitRows(rows);
    if (success) {
      setCsvPreview(null);
    }
  }

  async function handleCsvFile(file: File) {
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.length < 2) {
      setImportError("CSV must include a header row and at least one data row.");
      setCsvPreview(null);
      return;
    }
    const headers = parsed[0].map((header) => header.trim().toLowerCase());
    const headerIndex = {
      name: headers.indexOf("name"),
      email: headers.indexOf("email"),
      phone: headers.indexOf("phone"),
      roles: headers.indexOf("roles"),
    };
    if (Object.values(headerIndex).some((value) => value === -1)) {
      setImportError("CSV headers must include: name, email, phone, roles.");
      setCsvPreview(null);
      return;
    }
    const rows = parsed.slice(1).map((cells) => ({
      name: cells[headerIndex.name] ?? "",
      email: cells[headerIndex.email] ?? "",
      phone: cells[headerIndex.phone] ?? "",
      role_ids: [],
      roles_raw: cells[headerIndex.roles] ?? "",
      errors: [],
    }));
    setCsvPreview({ headers, rows });
    setImportError(null);
    setSummary(null);
  }

  return (
    <div className="stack page">
      <div className="page-header">
        <h2>Add Crew Accounts</h2>
        <p>Create multiple crew accounts manually or import them from CSV. New crew accounts use the temporary password <code>CrewOps123!</code>.</p>
      </div>

      <div className="panel account-mode-switcher">
        <button type="button" className={mode === "manual" ? "nav-link active" : "nav-link"} onClick={() => setMode("manual")}>Manual Entry</button>
        <button type="button" className={mode === "csv" ? "nav-link active" : "nav-link"} onClick={() => setMode("csv")}>Import CSV</button>
        <button type="button" className="ghost" onClick={downloadCsvTemplate}>Download CSV Template</button>
      </div>

      {mode === "manual" ? (
        <div className="panel">
          <table className="data-table account-entry-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Roles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {validatedManualRows.map((row, index) => (
                <tr key={`manual-row-${index}`}>
                  <td><input value={row.name} onChange={(event) => updateManualRow(index, { name: event.target.value })} /></td>
                  <td><input value={row.email} onChange={(event) => updateManualRow(index, { email: event.target.value })} /></td>
                  <td><input value={row.phone} onChange={(event) => updateManualRow(index, { phone: event.target.value })} /></td>
                  <td>
                    <div className="role-checkboxes table-role-list">
                      {activeRoles.map((role) => (
                        <label key={`${index}-${role.id}`} className="checkbox-chip">
                          <input
                            type="checkbox"
                            checked={row.role_ids.includes(role.id)}
                            onChange={() => toggleManualRole(index, role.id)}
                          />
                          <span>{role.name}</span>
                        </label>
                      ))}
                    </div>
                    {row.errors.length > 0 ? <ul className="flat-list row-error-list">{row.errors.map((error) => <li key={error}>{error}</li>)}</ul> : null}
                  </td>
                  <td>
                    <button type="button" className="ghost compact-button" onClick={() => setManualRows((current) => current.filter((_, rowIndex) => rowIndex !== index))} disabled={validatedManualRows.length === 1}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="selected-crew-actions">
            <button type="button" className="ghost" onClick={() => setManualRows((current) => [...current, emptyRow()])}>Add Row</button>
            <button type="button" onClick={handleManualSave}>Save All Rows</button>
          </div>
        </div>
      ) : (
        <div className="panel stack">
          <label>
            CSV File
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  void handleCsvFile(file);
                }
              }}
            />
          </label>
          <p className="muted">Expected headers: <code>name,email,phone,roles</code>. Use <code>|</code> between multiple role names.</p>
          {importError ? <p className="error-banner">{importError}</p> : null}
          {csvPreview ? (
            <>
              <table className="data-table account-entry-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Roles</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {validatedCsvRows.map((row, index) => (
                    <tr key={`csv-row-${index}`}>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td>{row.phone}</td>
                      <td>{row.roles_raw || "—"}</td>
                      <td>{row.errors.length > 0 ? <ul className="flat-list row-error-list">{row.errors.map((error) => <li key={error}>{error}</li>)}</ul> : <span className="muted">Ready</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="selected-crew-actions">
                <button type="button" className="ghost" onClick={() => setCsvPreview(null)}>Clear Preview</button>
                <button type="button" onClick={handleCsvImport}>Import Validated Rows</button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {summary ? <p className={summary.startsWith("Created") ? "success-banner" : "error-banner"}>{summary}</p> : null}
    </div>
  );
}
